/**
 * <ma-ingredient-carousel> — arrows, dots, mouse drag-to-scroll and optional autoplay
 * over a CSS scroll-snap track. Touch swipes are native scrolling; without JS the
 * track still scrolls and only arrows/dots are hidden.
 *   data-autoplay="4000"  → advance every 4s (pauses on hover/focus/drag, off for reduced-motion)
 */
if (!customElements.get('ma-ingredient-carousel')) {
  class MaIngredientCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.ma-how-it-works__track');
      this.cards = [...this.querySelectorAll('.ma-how-it-works__card')];
      this.dots = [...this.querySelectorAll('.ma-how-it-works__dot')];
      if (!this.track || this.cards.length < 2) return;
      this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.classList.add('is-ready');

      this.querySelectorAll('.ma-how-it-works__arrow').forEach((b) =>
        b.addEventListener('click', () => { this.go(this.index() + Number(b.dataset.dir)); this.restart(); })
      );
      this.dots.forEach((d) => d.addEventListener('click', () => { this.go(Number(d.dataset.i)); this.restart(); }));
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(() => this.sync());
      }, { passive: true });

      // mouse drag-to-scroll (touch/pen already scroll natively)
      let startX = 0, startLeft = 0, moved = false;
      this.track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        startX = e.clientX; startLeft = this.track.scrollLeft; moved = false;
        this.track.setPointerCapture(e.pointerId);
        this.classList.add('is-dragging');
        this.stop();
      });
      this.track.addEventListener('pointermove', (e) => {
        if (!this.classList.contains('is-dragging')) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 4) moved = true;
        this.track.scrollLeft = startLeft - dx;
      });
      const end = (e) => {
        if (!this.classList.contains('is-dragging')) return;
        this.classList.remove('is-dragging');
        const dx = e.clientX - startX, last = this.stops().length - 1, i = this.index();
        if (dx < -40 && i === last && startLeft >= this.track.scrollWidth - this.track.clientWidth - 2) this.go(i + 1); // drag on past the end: loop
        else if (dx > 40 && i === 0 && startLeft <= 2) this.go(-1);
        else this.go(i); // settle on the nearest card
        this.restart();
      };
      this.track.addEventListener('pointerup', end);
      this.track.addEventListener('pointercancel', end);
      this.track.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);

      // autoplay
      this.interval = Number(this.dataset.autoplay) || 0;
      if (this.interval && !this.reduce) {
        ['mouseenter', 'focusin', 'touchstart'].forEach((ev) => this.addEventListener(ev, () => this.stop(), { passive: true }));
        ['mouseleave', 'focusout', 'touchend'].forEach((ev) => this.addEventListener(ev, () => this.start(), { passive: true }));
        document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.start()));
        new IntersectionObserver(([e]) => (e.isIntersecting ? this.start() : this.stop()), { threshold: 0.3 }).observe(this);
      }
      window.MaLoop && window.MaLoop.edges(this.track, { next: () => { this.go(this.index() + 1); this.restart(); }, prev: () => { this.go(this.index() - 1); this.restart(); } });
      window.addEventListener('resize', () => this.sync());
      this.sync();
    }
    disconnectedCallback() { this.stop(); }
    start() { if (this.interval && !this.reduce && !this._timer) this._timer = setInterval(() => this.go(this.index() + 1), this.interval); }
    stop() { clearInterval(this._timer); this._timer = null; }
    restart() { this.stop(); this.start(); }
    // Snap stops the track can actually reach: with 2+ cards in view the last few
    // share the end position, so they collapse into one stop (and one dot).
    stops() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const base = this.cards[0].offsetLeft;
      const out = [];
      this.cards.forEach((c) => {
        const p = Math.min(Math.max(c.offsetLeft - base, 0), max);
        // a final nudge shorter than half a card isn't worth its own dot: fold it into the previous stop
        if (!out.length) out.push(p);
        else if (p - out[out.length - 1] > this.cards[0].offsetWidth / 2) out.push(p);
        else out[out.length - 1] = p;
      });
      return out;
    }
    index() {
      const x = this.track.scrollLeft;
      let best = 0;
      this.stops().forEach((p, i, a) => { if (Math.abs(p - x) < Math.abs(a[best] - x)) best = i; });
      return best;
    }
    go(i) {
      const s = this.stops();
      const n = s.length;
      const k = ((i % n) + n) % n;
      // wrapping dissolves round to the other end (with the loop cue) instead of rewinding
      if (k !== i && window.MaLoop) return window.MaLoop.jump(this.track, { left: s[k], host: this.track.parentElement, text: `${k + 1} / ${n}` });
      this.track.scrollTo({ left: s[k], behavior: this.reduce ? 'auto' : 'smooth' });
    }
    sync() {
      const n = this.stops().length;
      const i = this.index();
      this.dots.forEach((d, j) => {
        d.hidden = j >= n;
        d.setAttribute('aria-selected', String(j === i));
      });
    }
  }
  customElements.define('ma-ingredient-carousel', MaIngredientCarousel);
}
